import { Injectable } from '@angular/core';
import { LoadingController, ToastController, AlertController} from '@ionic/angular';
import { LogService } from './log.service';
import { GenericService } from './generic.service';
import { LogLevel } from './enum';
import { GlobalService } from './global.service';
import { NetworkService, CONNECTION_ERROR } from './network.service';
import { AppConfig } from './app.config';
import { from } from 'rxjs';

@Injectable()
export class IonicService
{
    isLoading = false;

    loader: HTMLIonLoadingElement;
    private overlayLoader: HTMLIonLoadingElement;
    subscription: any;
    searchText: string;
    dataFetcherEvent: any;

    constructor(private loadingCtrl: LoadingController, private toastCtrl: ToastController,
        public alertCtrl: AlertController, private ls: LogService)
    {

    }

    private async safeDismissElement(loader: HTMLIonLoadingElement): Promise<void>
    {
        if (!loader)
        {
            return;
        }
        try
        {
            await loader.dismiss();
        }
        catch (ex)
        {
        }
    }

    private async safeDismissAllOverlays(): Promise<void>
    {
        try
        {
            await this.loadingCtrl.dismiss();
        }
        catch (ex)
        {
        }
    }

   async showLoader(text:string=null, allowCancel=false)
   {
       try
       {
           if (this.loader)
               this.loader.textContent = text;
           else
           {
               text = text || 'Please wait a moment...';
               if (allowCancel)
                   this.loader = await this.loadingCtrl.create({ message: text, showBackdrop: true });
               else
                   this.loader = await this.loadingCtrl.create({ message: text });
               return await this.loader.present();
           }
       }
       catch (ex)
       {
       }
    }

    async hideLoader()
    {
        try
        {
            await this.safeDismissElement(this.loader);
            this.loader = null;
            await this.safeDismissAllOverlays();
        }
        catch (ex)
        {
        }
   }

    async presentLoader()
    {
        try
        {
            this.isLoading = true;
            if (this.overlayLoader)
            {
                return;
            }
            this.overlayLoader = await this.loadingCtrl.create({});
            await this.overlayLoader.present();
            if (!this.isLoading && this.overlayLoader)
            {
                await this.safeDismissElement(this.overlayLoader);
                this.overlayLoader = null;
            }
        }
        catch (ex)
        {
        }
    }

    async dismissLoader()
    {
        try
        {
            this.isLoading = false;
            await this.safeDismissElement(this.overlayLoader);
            this.overlayLoader = null;
            await this.safeDismissAllOverlays();
        }
        catch (ex)
        {
        }
    }

    /** Clears overlay and message loaders (e.g. on forced logout). */
    async clearLoaders()
    {
        this.isLoading = false;
        await this.safeDismissElement(this.overlayLoader);
        this.overlayLoader = null;
        await this.safeDismissElement(this.loader);
        this.loader = null;
        await this.safeDismissAllOverlays();
    }

    get spinner()
    {
        return GlobalService.spinner;
    }

    showSpinner()
    {
        GlobalService.spinner = true;
    }

    hideSpinner()
    {
        GlobalService.spinner = false;
    }

   async showToast(text, position:any = 'top', cssClass = null)
   {
       try
       {
           await this.hideLoader();
           await this.hideSpinner();
           var toast = await this.toastCtrl.create({
               message: text,
               duration: 3000,
               position: position,
               cssClass: cssClass
           });
           await toast.present();
       }
       catch (ex)
       {
       }
    }

    async showSuccessToast(text:string , position = null)
    {
        try
        {
            await this.showToast(text, position, 'success-toast');
        }
        catch (ex)
        {
        }
    }

    async showErrorToast(err: any, position = null, errorLevel: LogLevel = null)
    {
        try
        {
            let msg = GenericService.fetchErrorMessage(err);
            if(msg.includes("419 Session Expired")){
              msg = "Session Expired !";
            }
            await this.showToast(msg, position, 'error-toast');
            if (errorLevel < 0 || errorLevel > 0)
                this.ls.log(err);
        }
        catch (ex)
        {
        }
    }

   async alert(text: string, cssClass=null)
    {
       await this.hideLoader();
       await this.hideSpinner();
       let obj:any = {
           message: text,
           buttons: ['Ok'],
       };
       if (cssClass)
           obj.cssClass= cssClass

        let alert = await this.alertCtrl.create(obj);
        await alert.present();
   }

   async alertSuccess(text: string)
    {
      await this.alert(text, 'alert-success');
    }

    async alertError(err: any, remarks:string=null, log=true)
    {
        await this.hideLoader();
        await this.hideSpinner();

        let msg: string = GenericService.fetchErrorMessage(err);
        if (msg.startsWith('ServerError: '))
        {
            msg = msg.replace('ServerError: ', '');
            log = false;
        }
        await this.alert(remarks || msg, 'alert-error');
        if (log)
            this.ls.log(err);
    }

    showAndLogError(err, remarks=null, silentMode=false)
    {
        remarks = remarks || "Something went wrong";
        if (err == CONNECTION_ERROR)
            remarks = err;
        if (!silentMode) this.alertError(err, remarks, true);
        else
            this.ls.log(err, LogLevel.Error, null, remarks);
        this.hideLoader();
        return err;
    }

    throwCustomError(err, remarks, logErr=true)
    {
        if (err == CONNECTION_ERROR)
            remarks = err;
        remarks = remarks || "Something went wrong";
        if (logErr)
            this.ls.log(err, LogLevel.Error, null, remarks);

        throw new Error(err);
    }

    async alertEnableData()
    {
        await this.hideLoader();
        await this.hideSpinner();

        if (NetworkService.isOffline())
        {
            let prompt = await this.alertCtrl.create({
                header: 'Turn on Mobile data?',
                message:'Connect to a Wi-Fi network or turn on Mobile data.',
                buttons: [
                    {
                        text: "Ok",
                    }
                ]
            });
            await prompt.present();
        }
    }
}
